import { useState } from "react";
import { Mail, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { requestMagicLink, startGoogleLogin } from "@/lib/auth";

export const GoogleG = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z" />
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.2v3.1C3.3 21.4 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.2C.4 8.2 0 10 0 12s.4 3.8 1.2 5.4l4.1-3.1z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.3 0 3.3 2.6 1.2 6.6l4.1 3.1c.9-2.9 3.6-5 6.7-5z" />
  </svg>
);

// Shared sign-in options: Google + passwordless email link. Used by the dialog and the board's sign-in wall.
export const SignInOptions = ({ idPrefix = "auth", next, compact = false, onSent }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = await requestMagicLink(email.trim().toLowerCase(), next);
      setSent(body.message);
      onSent?.(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid={`${idPrefix}-magic-sent`} role="status" className="border border-[#0FBF77] bg-[#F0FBF6] p-4 text-left">
        <p className="inline-flex items-center gap-2 font-display text-base font-bold text-[#0F141B]"><MailCheck size={16} aria-hidden="true" className="text-[#0A8F5A]" />Check your inbox</p>
        <p className="mt-1.5 text-sm text-[#3D4049] leading-relaxed">We sent a sign-in link to <b data-testid={`${idPrefix}-magic-sent-email`}>{email}</b>. Open it on this device and you're in. It works once and expires in 15 minutes.</p>
        <button type="button" data-testid={`${idPrefix}-magic-resend`} onClick={() => setSent(null)} className="mt-3 text-xs underline underline-offset-4 text-[#0F141B]">Use a different email</button>
      </div>
    );
  }

  return (
    <div className="space-y-3 text-left">
      <form data-testid={`${idPrefix}-magic-form`} onSubmit={submit} className={`flex ${compact ? "flex-col sm:flex-row" : "flex-col"} gap-2`}>
        <label htmlFor={`${idPrefix}-email`} className="sr-only">Email address</label>
        <Input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" required autoComplete="email" inputMode="email" maxLength={120} placeholder="you@company.com" value={email} onChange={(event) => setEmail(event.target.value)} className="h-12 rounded-none border-[#D2D5DA] bg-white text-sm flex-1" />
        <button type="submit" data-testid={`${idPrefix}-magic-submit`} disabled={busy} className="chrome-btn-dark chrome-btn-dark-glow inline-flex items-center justify-center gap-2 h-12 px-5 font-mono-label text-[10px] !tracking-normal whitespace-nowrap disabled:opacity-60"><Mail size={14} aria-hidden="true" />{busy ? "Sending…" : "Email me a sign-in link"}</button>
      </form>
      {error && <p data-testid={`${idPrefix}-magic-error`} role="alert" className="text-xs text-[#B42318]">{error}</p>}
      <div className="flex items-center gap-3 text-[10px] font-mono-label !tracking-normal text-[#8A94A2]"><span className="h-px flex-1 bg-[#E3E6EA]" />or<span className="h-px flex-1 bg-[#E3E6EA]" /></div>
      <button type="button" data-testid={`${idPrefix}-google-btn`} onClick={startGoogleLogin} className="chrome-btn w-full inline-flex items-center justify-center gap-2.5 h-12 px-5 font-mono-label text-[10px] !tracking-normal"><GoogleG />Continue with Google</button>
      <p className="text-[11px] leading-relaxed text-[#71747B]">No password to remember. Signing in creates a free DCYB member account so you can save roles, track applications and request intros.</p>
    </div>
  );
};
